/* istanbul ignore file */

// We're ignoring this file because jsdom does not have good support for SVG elements.
// As a result we'd have to mock the `getTotalLength` and `getPointAtLength` methods which would
// end up being 1more work and 1less useful than just testing the logic in this file normally

import { ScaleTime } from 'd3-scale';
import _ from 'lodash';
import { LiveWindPoint } from 'types/liveWind';

type Data = Array<LiveWindPoint>;
type DashRange = { start: number; end: number };
type DashRanges = Array<DashRange>;

/**
 * The code here was taken from this blog post:
 * https://wiredcraft.com/blog/dashed-line-segmentation-d3js/
 * The idea is to create a stroke-dasharray like `1 1 1 4 384 2`
 * to create different sized dashes and gaps to simulate having a solid and a dashed line.
 *
 * Be careful modifying this code. I couldn't really find another way to do this, given that we
 * can't just render multiple lines since that would make the hover dot state really complicated and
 * also be complicated to do color gradients between the lines.
 */

const DASH_LENGTH = 6;
const DASH_SEPARATOR_LENGTH = 4;

const isDashed = (d: LiveWindPoint) => !d.sensorWind;

const getDashedRanges = (data: Data) => {
  const hasOpenRange = (arr: DashRanges) => _.last(arr) && _.last(arr)?.end === -1;
  const lastIndex = data.length - 1;

  return _.reduce<LiveWindPoint, DashRanges>(
    data,
    (res, d, i) => {
      // Check if we're starting a new dashed segment
      const isRangeStart = !hasOpenRange(res) && isDashed(d);
      if (isRangeStart) {
        res.push({ start: Math.max(0, i - 1), end: -1 });
      }

      // Check if we're ending a dashed segment
      const isRangeEnd = hasOpenRange(res) && (!isDashed(d) || i === lastIndex);
      if (isRangeEnd) res[res.length - 1].end = i;

      return res;
    },
    [],
  );
};

// approximates the length of a path element at given x coordinate so we know how long to make the
// dashed and undashed segments
const getPathLengthAtX = (path: SVGPathElement, x: number) => {
  const EPSILON = 1;
  let point;
  let target;
  let start = 0;
  let end = path.getTotalLength();

  // perform a binary search for the point on the path
  while (true) {
    target = Math.floor((start + end) / 2);
    point = path.getPointAtLength(target);

    if (Math.abs(point.x - x) <= EPSILON) break;

    if ((target >= end || target <= start) && point.x !== x) {
      break;
    }

    if (point.x > x) {
      end = target;
    } else if (point.x < x) {
      start = target;
    } else {
      break;
    }
  }

  return target;
};

const getDashedSegment = (length: number) => {
  const totalDashLen = DASH_LENGTH + DASH_SEPARATOR_LENGTH;
  const dashCount = Math.floor(length / totalDashLen);
  return _.range(dashCount)
    .map(() => `${DASH_SEPARATOR_LENGTH}, ${DASH_LENGTH}`)
    .concat(`${length - dashCount * totalDashLen}`)
    .join(', ');
};

const buildDashArray = (dashedRanges: DashRanges, lengths: number[]) => {
  const dashArray = _.reduce<DashRange, Array<string>>(
    dashedRanges,
    (res, { start, end }, i) => {
      const prevEnd = i === 0 ? 0 : dashedRanges[i - 1].end;

      const normalSegment = `${lengths[start] - lengths[prevEnd]}`;
      const dashedSegment = getDashedSegment(lengths[end] - lengths[start]);

      return res.concat([normalSegment, dashedSegment]);
    },
    [],
  );

  if (dashedRanges[dashedRanges.length - 1].end !== lengths.length - 1) {
    dashArray.push(
      `${lengths[lengths.length - 1] - lengths[dashedRanges[dashedRanges.length - 1].end]}`,
    );
  }

  return dashArray;
};

const getDashArray = (data: Data, path: SVGPathElement, xScale: ScaleTime<number, number>) => {
  const dashedRanges = getDashedRanges(data);
  if (dashedRanges.length === 0) return null;

  const lengths = data.map((d) => getPathLengthAtX(path, xScale(d.timestamp)));
  return buildDashArray(dashedRanges, lengths).join(', ');
};

export default getDashArray;
